import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import qs from "qs";
import isEqual from "lodash/isEqual";
import isArray from "lodash/isArray";
import mapValues from "lodash/mapValues";
import isPlainObject from "lodash/isPlainObject";

import CreationFlow from "wonderbly-components/lib/CreationFlow";
import RichText from "../../Prismic/RichText";

import { useCountry, useErrorMessages, useQueryParams } from "../../../hooks";

import formatName from "src/common/utils/format-adventurer-name";
import { validate, isValid } from "src/client/js/facades/eagle/validation";
import { localiseUrlII } from "src/common/utils/format-localised-url";
import { replaceRichText } from "src/common/utils/text-replace";
import { trackErrorCanvas } from "../canvasTracking";

import { findSelectedSteps } from "./helpers";

import logger from "src/logger";

const formatValues = (values) => {
  if (!isPlainObject(values) && !isArray(values)) {
    return values;
  }

  return mapValues(values, (value, key) => {
    if (isPlainObject(value)) {
      return formatValues(value);
    }
    // multiTextField arrays uses arrays of objects
    // but muse wants an array of strings
    if (isArray(value) && value[0]?.multiTextValue !== undefined) {
      return value.map((v) => v.multiTextValue);
    }
    if (isArray(value)) {
      return value.map(formatValues);
    }
    if (key === "name" || key === "surname") {
      return formatName(value);
    }

    return value;
  });
};

// We map certain values on the outbound request to validation to suit
// the product's schema. On the inbound we need to map back to the state
// the form model is expecting
const remapErrors = (errors, values) => {
  return errors.map((error) => {
    const { message, name, type } = error;
    const path = error.name.split(".");
    const value = values[path[0]];

    if (value && isArray(value) && value[0]?.multiTextValue !== undefined) {
      return {
        message,
        name: `${[path[0]]}[${path[1]}].multiTextValue`,
        type,
      };
    }

    return {
      message,
      name,
      type,
    };
  });
};

const ProductHeroIIForm = ({
  product,
  banner,
  steps,
  previousStepCta,
  closeModalCta,
  setIsVisible,
  externalPresetValues,
}) => {
  const creationFlowRef = useRef();
  const country = useCountry();
  const { params } = useQueryParams();
  const { joiErrorMessages, getErrorMessage, sendErrorMessage } =
    useErrorMessages();
  const [activeSteps, setActiveSteps] = useState(steps);

  function closeCreationFlow() {
    setIsVisible(false);
  }

  function handleBackButtonClick() {
    if (!isEqual(activeSteps, steps)) {
      setActiveSteps(steps);
      return;
    }

    closeCreationFlow();
  }

  async function validateStep(values, actions) {
    if (typeof values.step_count !== "undefined") {
      if (values.step_count > 0) {
        return;
      }
      actions.setError("step_count", {
        type: "required",
        message: getErrorMessage("general.required"),
      });

      return false;
    }

    try {
      const { body } = await validate({
        item: {
          productId: product.main.productId,
          customisations: {
            ...formatValues(values),
            locale: country.locale,
            product_slug: product.main.productId,
          },
        },
      });

      return isValid(body.data, values);
    } catch (err) {
      // validate() can throw a RestResponseError or network Error, not a Joi errors array
      if (!isArray(err)) {
        logger.error(err, "Unexpected error validating product creation step");
        sendErrorMessage();

        return false;
      }

      const errors = joiErrorMessages(err);
      const remappedErrors = remapErrors(errors, values);

      actions.setErrors(
        remappedErrors.reduce((acc, { name, ...error }) => {
          acc[name] = error;
          return acc;
        }, {})
      );

      remappedErrors.map((error) => {
        trackErrorCanvas(error.name, error.message);
      });

      return false;
    }
  }

  function handleCreationFlowSubmit(values) {
    if (values.step_count) {
      const newSteps = findSelectedSteps(activeSteps, values.step_count);
      setActiveSteps(newSteps);
      return;
    }

    // Creating openCreationFlow variable to remove it from the rest of the params
    // eslint-disable-next-line no-unused-vars
    const { orderNumber, itemId, openCreationFlow, ...rest } = params;

    const productSlug = product.productInformation.productSlug;
    const search = qs.stringify(
      {
        ...rest,
        ...formatValues(values),
      },
      { addQueryPrefix: true }
    );

    let url = `/personalized-products/${productSlug}/preview`;

    if (orderNumber) {
      url += `/${orderNumber}`;
      if (!itemId) {
        logger.error("Order number was provided without an item number", {
          product_slug: productSlug,
          query_params: params,
        });
        // TODO: Add base error message to form
        return;
      }
    }

    if (itemId) {
      url += `/${itemId}`;
    }
    url += search;

    window.location = localiseUrlII(country, url);
  }

  function getFlowValues() {
    if (!creationFlowRef.current) {
      return {};
    }

    return creationFlowRef.current.values;
  }

  return (
    <CreationFlow
      ref={creationFlowRef}
      banner={banner}
      steps={activeSteps.map((step) => ({
        ...step,
        get content() {
          const values = getFlowValues();
          return <RichText fields={replaceRichText(step.content, values)} />;
        },
        onSubmit: validateStep,
      }))}
      backStepCta={previousStepCta}
      backToCta={closeModalCta}
      handleBackOnFirstStep={handleBackButtonClick}
      handleCloseClick={closeCreationFlow}
      handleSubmit={handleCreationFlowSubmit}
      externalPresetValues={externalPresetValues}
    />
  );
};

ProductHeroIIForm.propTypes = {
  banner: PropTypes.string.isRequired,
  steps: PropTypes.array.isRequired,
  previousStepCta: PropTypes.string.isRequired,
  closeModalCta: PropTypes.string.isRequired,
  product: PropTypes.shape({
    main: PropTypes.shape({
      productId: PropTypes.string.isRequired,
      canvas2: PropTypes.bool,
    }),
    productInformation: PropTypes.shape({
      productSlug: PropTypes.string.isRequired,
    }),
  }).isRequired,
  setIsVisible: PropTypes.func.isRequired,
  externalPresetValues: PropTypes.shape({
    locale: PropTypes.string,
  }),
};

export default ProductHeroIIForm;
